import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  Paper,
  Chip,
  Grid2 as Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  InputAdornment,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Send,
  AttachFile,
  MoreVert,
  ArrowBack,
  Schedule,
  CheckCircle,
  Person,
  Assignment,
  Build,
  Info,
  Warning,
  Delete,
  Download,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const PpMessageThreadPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Mock conversation and messages
  const mockConversation = {
    id: threadId,
    type: "submission",
    subject: "Plan Review Questions - PRS001",
    participants: [
      {
        id: "PP001",
        name: "John Smith, PE",
        role: "private_provider",
        avatar: "JS",
        online: true,
      },
      {
        id: "C001",
        name: "ABC Construction",
        role: "contractor",
        avatar: "AB",
        online: false,
        lastSeen: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      },
    ],
    relatedSubmission: "PRS001",
    status: "active",
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  };

  const mockMessages = [
    {
      id: "MSG001",
      text: "Hello! I have some questions about the foundation details in the structural plans.",
      sender: {
        id: "C001",
        name: "ABC Construction",
        role: "contractor",
      },
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      attachments: [],
      read: true,
    },
    {
      id: "MSG002",
      text: "Hi! I'd be happy to help. Can you be more specific about which foundation details you need clarification on?",
      sender: {
        id: "PP001",
        name: "John Smith, PE",
        role: "private_provider",
      },
      timestamp: new Date(
        Date.now() - 2 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000
      ).toISOString(),
      attachments: [],
      read: true,
    },
    {
      id: "MSG003",
      text: 'Specifically, I need clarification on the footing depth requirements. The plans show 24" but I want to confirm this meets local code for our soil conditions.',
      sender: {
        id: "C001",
        name: "ABC Construction",
        role: "contractor",
      },
      timestamp: new Date(
        Date.now() - 2 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000
      ).toISOString(),
      attachments: ["soil_report.pdf"],
      read: true,
    },
    {
      id: "MSG004",
      text: 'Thanks for the soil report. Based on the soil conditions shown, 24" footing depth is adequate per IBC 1805.4.2. You can proceed with construction.',
      sender: {
        id: "PP001",
        name: "John Smith, PE",
        role: "private_provider",
      },
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      attachments: [],
      read: true,
    },
    {
      id: "MSG005",
      text: "Perfect! Thank you for the quick response. I have uploaded the revised structural plans addressing the foundation details.",
      sender: {
        id: "C001",
        name: "ABC Construction",
        role: "contractor",
      },
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      attachments: ["Structural_Plans_Rev01.pdf"],
      read: false,
    },
  ];

  useEffect(() => {
    setConversation(mockConversation);
    setMessages(mockMessages);
  }, [threadId]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(attachments.filter((a) => a.id !== attachmentId));
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return "just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    try {
      setSending(true);

      const messageData = {
        id: `MSG${Date.now()}`,
        text: newMessage,
        sender: {
          id: "PP001",
          name:
            currentUser?.user?.firstName + " " + currentUser?.user?.lastName,
          role: "private_provider",
        },
        timestamp: new Date().toISOString(),
        attachments: attachments.map((a) => a.name),
        read: true,
      };

      // Simulate sending delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      setMessages((prev) => [...prev, messageData]);
      setNewMessage("");
      setAttachments([]);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map((file) => ({
      id: `ATT${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
      file,
    }));

    setAttachments([...attachments, ...newAttachments]);
  };

  const isCurrentUser = (senderId) => {
    return senderId === "PP001"; // Current PP user
  };

  const getParticipantStatus = (participant) => {
    if (participant.online) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Online"
          color="success"
          size="small"
        />
      );
    } else if (participant.lastSeen) {
      const lastSeenTime = formatTimeAgo(participant.lastSeen);
      return (
        <Chip
          label={`Last seen ${lastSeenTime}`}
          color="default"
          size="small"
          variant="outlined"
        />
      );
    }
    return (
      <Chip label="Offline" color="default" size="small" variant="outlined" />
    );
  };

  if (!conversation) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Conversation not found.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px - 100px)", // Account for top nav (64px) and breadcrumbs (60px)
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "absolute",
        top: "105px", // Start after top nav (64px) + breadcrumbs (60px)
        left: { lg: "280px", xs: 0 }, // Account for sidebar on large screens
        right: 0,
        bottom: 0,
        zIndex: 1,
      }}
    >
      {/* Header - Fixed height */}
      <Card sx={{ borderRadius: 0, flexShrink: 0 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/pp/messaging")}>
              <ArrowBack />
            </IconButton>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">{conversation.subject}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {conversation.participants
                  .filter((p) => p.id !== "PP001")
                  .map((participant) => (
                    <Box
                      key={participant.id}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Avatar
                        sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
                      >
                        {participant.avatar}
                      </Avatar>
                      <Typography variant="body2">
                        {participant.name}
                      </Typography>
                      {getParticipantStatus(participant)}
                    </Box>
                  ))}
              </Box>
            </Box>

            {conversation.relatedSubmission && (
              <Chip
                label={`Submission: ${conversation.relatedSubmission}`}
                color="primary"
                variant="outlined"
                icon={<Assignment />}
                clickable
                onClick={() =>
                  navigate(`/pp/submissions/${conversation.relatedSubmission}`)
                }
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Messages - Scrollable area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 2,
          bgcolor: "grey.50",
          minHeight: 0,
        }}
      >
        <List>
          {messages.map((message, index) => {
            const isOwn = isCurrentUser(message.sender.id);
            const showAvatar =
              index === 0 ||
              messages[index - 1].sender.id !== message.sender.id;

            return (
              <ListItem
                key={message.id}
                sx={{
                  display: "flex",
                  flexDirection: isOwn ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                {showAvatar && (
                  <Avatar
                    sx={{
                      bgcolor: isOwn ? "primary.main" : "secondary.main",
                      mx: 1,
                      width: 36,
                      height: 36,
                    }}
                  >
                    {message.sender.role === "private_provider" ? "PP" : "C"}
                  </Avatar>
                )}

                <Paper
                  sx={{
                    p: 2,
                    maxWidth: "70%",
                    bgcolor: isOwn ? "primary.main" : "background.paper",
                    color: isOwn ? "primary.contrastText" : "text.primary",
                    ml: !showAvatar && !isOwn ? 6 : 0,
                    mr: !showAvatar && isOwn ? 6 : 0,
                    borderRadius: 2,
                    ...(isOwn
                      ? {
                          borderBottomRightRadius: 4,
                        }
                      : {
                          borderBottomLeftRadius: 4,
                        }),
                  }}
                >
                  {showAvatar && (
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ opacity: 0.8 }}
                    >
                      {message.sender.name}
                    </Typography>
                  )}

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {message.text}
                  </Typography>

                  {message.attachments.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {message.attachments.map((attachment, i) => (
                        <Chip
                          key={i}
                          icon={<AttachFile />}
                          label={attachment}
                          size="small"
                          variant="outlined"
                          sx={{
                            mr: 1,
                            mb: 1,
                            bgcolor: isOwn
                              ? "rgba(255,255,255,0.2)"
                              : "action.hover",
                            color: isOwn ? "inherit" : "text.primary",
                          }}
                          clickable
                        />
                      ))}
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      opacity: 0.7,
                      mt: 1,
                    }}
                  >
                    {formatMessageTime(message.timestamp)}
                    {isOwn && (
                      <CheckCircle
                        sx={{ fontSize: 12, ml: 0.5 }}
                        color={message.read ? "success" : "disabled"}
                      />
                    )}
                  </Typography>
                </Paper>
              </ListItem>
            );
          })}
          <div ref={messagesEndRef} />
        </List>

        {typing && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, ml: 7, mb: 2 }}
          >
            <Avatar sx={{ width: 24, height: 24, bgcolor: "secondary.main" }}>
              C
            </Avatar>
            <Paper sx={{ p: 1, px: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Contractor is typing...
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Message Input - Fixed height */}
      <Card sx={{ borderRadius: 0, flexShrink: 0 }}>
        <CardContent sx={{ py: 2 }}>
          {/* Attachments Preview */}
          {attachments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Attachments ({attachments.length}):
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {attachments.map((attachment) => (
                  <Chip
                    key={attachment.id}
                    label={`${attachment.name} (${attachment.size})`}
                    size="small"
                    onDelete={() => handleRemoveAttachment(attachment.id)}
                    deleteIcon={<Delete />}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      style={{ display: "none" }}
                      id="message-file-upload"
                      onChange={handleFileUpload}
                    />
                    <IconButton
                      component="label"
                      htmlFor="message-file-upload"
                      size="small"
                    >
                      <AttachFile />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={
                (!newMessage.trim() && attachments.length === 0) || sending
              }
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                "&:disabled": { bgcolor: "grey.300" },
              }}
            >
              <Send />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PpMessageThreadPage;
